const CONSENT_KEY = "digifist-consent-v1";
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE = 180 * 24 * 60 * 60 * 1000;
const DENIED = Object.freeze({ analytics: false, marketing: false });
const LOCALES = new Set(["en", "nl-be", "tr-tr"]);
const LEAD_TYPES = new Set(["contact", "growth", "dynamic"]);
const CAMPAIGNS = {
  utm_source: new Set(["google", "bing", "facebook", "instagram", "linkedin", "newsletter"]),
  utm_medium: new Set(["cpc", "ppc", "paid_social", "social", "organic", "email", "referral"]),
};

function readConsent(raw, now = Date.now()) {
  if (typeof raw !== "string" || raw.length > 256) return null;
  try {
    const data = JSON.parse(raw);
    if (!data || data.version !== CONSENT_VERSION || typeof data.analytics !== "boolean" ||
      typeof data.marketing !== "boolean" || !Number.isFinite(data.savedAt) ||
      data.savedAt > now + 60_000 || now - data.savedAt > CONSENT_MAX_AGE) return null;
    return { analytics: data.analytics, marketing: data.marketing };
  } catch { return null; }
}

function serializeConsent(choices, now = Date.now()) {
  return JSON.stringify({ version: CONSENT_VERSION, savedAt: now,
    analytics: choices.analytics === true, marketing: choices.marketing === true });
}

function safePath(input) {
  if (typeof input !== "string" || input.length > 4096) return "/";
  try {
    const url = new URL(input, "https://www.digifist.com");
    const path = url.pathname;
    // Never copy arbitrary URL components, email addresses, encoded values or
    // free-form query text into events. Unusual routes share a generic bucket.
    if (url.username || url.password || path.length > 256 ||
      !/^\/[a-z0-9_./-]*$/i.test(path) || /\d{7,}/.test(path)) return "/";
    return path;
  } catch { return "/"; }
}

function safeReferrer(input) {
  try {
    const url = new URL(input);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password ? url.origin : "";
  } catch { return ""; }
}

function metaReferrerAllowed(input) {
  if (!input) return true;
  try {
    const url = new URL(input);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password &&
      !url.search && !url.hash && safePath(url.pathname) === url.pathname;
  } catch { return false; }
}

function safeCampaign(input) {
  const result = {};
  try {
    const url = new URL(input, "https://www.digifist.com");
    for (const [key, values] of Object.entries(CAMPAIGNS)) {
      const value = url.searchParams.get(key);
      if (value && values.has(value)) result[key === "utm_source" ? "campaign_source" : "campaign_medium"] = value;
    }
  } catch { /* No attribution is preferable to transmitting arbitrary text. */ }
  return result;
}

function serverContainerUrl(input, approvedHost) {
  if (!input) return "";
  try {
    const url = new URL(input);
    // Both values are deployment-owned. Approval requires the exact working
    // host; no arbitrary URL, credentials, query, path or obsolete Cloud Run host.
    if (url.protocol !== "https:" || url.username || url.password || url.port ||
      url.search || url.hash || url.pathname !== "/" || url.hostname !== approvedHost ||
      !/(?:^|\.)(?:digifist\.com|digifist\.com\.tr|stape\.io)$/.test(url.hostname)) return null;
    return url.origin;
  } catch { return null; }
}

function safeLocale(locale) { return LOCALES.has(locale) ? locale : "en"; }
function acknowledgedLead(response) { return response?.status === 200 && !response.error; }
function consentedFbp(cookies, now = Date.now()) {
  if (typeof cookies !== "string" || cookies.length > 16384) return undefined;
  const matches = cookies.split(";").map(value => value.trim()).filter(value => value.startsWith("_fbp="));
  if (matches.length !== 1) return undefined;
  const value = matches[0].slice(5);
  const match = /^fb\.1\.(\d{13})\.(\d{1,20})$/.exec(value);
  if (!match || Number(match[1]) > now + 300000 || now - Number(match[1]) > 90 * 86400000) return undefined;
  return value;
}
function safeLead(metadata) {
  if (!metadata || !LEAD_TYPES.has(metadata.leadType) ||
    typeof metadata.eventId !== "string" || !/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(metadata.eventId)) return null;
  return { event_id: metadata.eventId, lead_type: metadata.leadType,
    form_path: safePath(metadata.formPath), locale: safeLocale(metadata.locale) };
}

module.exports = { CONSENT_KEY, CONSENT_VERSION, CONSENT_MAX_AGE, DENIED,
  readConsent, serializeConsent, safePath, safeReferrer, safeCampaign,
  serverContainerUrl, safeLocale, acknowledgedLead, safeLead, consentedFbp, metaReferrerAllowed };
